import { createError, useNuxtApp } from '#imports'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { useSession } from './useSession'
import { useUserStore } from './useUserStore'

type RouteRedirection = Exclude<RouteLocationRaw, string>
type MiddlewareReturn = Exclude<RouteLocationRaw, string> | undefined

/**
 * Guest magic-link flow — token exchange and session establishment.
 *
 * Used by the global `auth` middleware for routes whose `meta.auth.mode` is
 * `'guest-magic-link'`. The route to redirect to when the flow fails is passed
 * in from `meta.auth.redirection` (still-guest users) and
 * `meta.auth.upgradedGuestRedirection` (users who upgraded their account).
 *
 * The route is expected to carry both a `token` (the magic-link access token
 * exchanged at `/auth/gateway`) and a `bm_user_id` (the user identifier, kept
 * and forwarded to the resend-link page on any redirect).
 *
 * - If a `token` is present, it is sent to `/auth/gateway`. If the token is
 *   valid (the request resolves), the session is established and the page is
 *   shown after the `token` is stripped from the URL. If the token is expired
 *   or invalid (the request rejects), the auth-service guest-status endpoint
 *   is queried to determine whether the user is still a guest:
 *     - still a guest → redirect to `resendLinkRedirection` (the resend-link page).
 *     - upgraded their account (no longer a guest) → redirect to
 *       `upgradedGuestRedirection`.
 * - If no `token` is present, the current guest status is checked. If the user
 *   is already authenticated as a guest, the page is shown. Otherwise, the
 *   user is redirected to `resendLinkRedirection` with `bm_user_id` forwarded.
 */
export function useGuestMagicLink() {
  const nuxtApp = useNuxtApp()
  const $httpFetch = useHttpFetch()
  const { isUserFetched, isUserGuest, fetchUser, user } = useUserStore()
  const { getSessionCookie } = useSession()

  async function handleGuestMagicLink(
    route: RouteLocationNormalized,
    resendLinkRedirection: RouteRedirection,
    upgradedGuestRedirection: RouteRedirection,
  ): Promise<MiddlewareReturn> {
    const { token: tokenQuery, ...queryWithoutToken } = route.query
    const token = Array.isArray(tokenQuery) ? tokenQuery[0] : tokenQuery
    const bmUserIdQuery = route.query.bm_user_id
    const bmUserId = Array.isArray(bmUserIdQuery)
      ? bmUserIdQuery[0]
      : bmUserIdQuery

    function redirectToResendLink(): MiddlewareReturn {
      if (!bmUserId) {
        throw createError({
          statusCode: 422,
          statusMessage:
            'Missing bm_user_id query param in guest magic link middleware',
        })
      }

      return {
        ...resendLinkRedirection,
        query: { ...resendLinkRedirection.query, bm_user_id: bmUserId },
      }
    }

    // A token is present in the URL: exchange it for a session via /auth/gateway.
    if (token) {
      try {
        await getSessionCookie(token)
        await nuxtApp.runWithContext(() => fetchUser())
      } catch {
        // The token is expired or invalid and we don't know why. Query the
        // auth-service guest-status endpoint to decide where to send the user.
        if (!bmUserId) {
          throw createError({
            statusCode: 422,
            statusMessage:
              'Missing bm_user_id query param in guest magic link middleware',
          })
        }

        try {
          const response = await nuxtApp.runWithContext(() =>
            $httpFetch('/auth/public/v1/users/:bmUserId/guest-status', {
              method: 'GET',
              pathParams: { bmUserId },
            }),
          )

          return response.is_guest
            ? redirectToResendLink()
            : upgradedGuestRedirection
        } catch (error) {
          const { status } = HttpApiError.fromAnyError(error)

          // 404 = user not found or deactivated = can't process the flow.
          if (status === 404) {
            throw createError({
              statusCode: 422,
              statusMessage: 'User not found in guest magic link middleware',
            })
          }

          return redirectToResendLink()
        }
      }

      // The token was valid. The navigation re-runs this middleware without
      // the token, which now resolves through the guest session established above.
      // Ensure bm_user_id is present in the URL so downstream pages can use it.
      return {
        path: route.path,
        query: {
          ...queryWithoutToken,
          bm_user_id: queryWithoutToken.bm_user_id ?? user.value.bmUserId,
        },
        hash: route.hash,
      }
    }

    // No token in the URL: only let guests through.
    if (!isUserFetched.value) {
      await nuxtApp.runWithContext(() => fetchUser())
    }

    if (isUserGuest.value) return undefined

    return redirectToResendLink()
  }

  return { handleGuestMagicLink }
}
